import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/orbit`;

const OrbitAuthContext = createContext();

export const OrbitAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('orbit-token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setUser(response.data);
    } catch {
      setUser(null);
      localStorage.removeItem('orbit-token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await axios.post(`${API}/auth/login`, 
        { email, password },
        { withCredentials: true }
      );
      setUser(response.data);
      localStorage.setItem('orbit-token', response.data.token);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      throw new Error(message);
    }
  };

  const register = async (name, email, password, sponsorCode = null, language = 'fr', isAffiliate = true) => {
    try {
      setError(null);
      const url = sponsorCode 
        ? `${API}/auth/register?sponsor_code=${sponsorCode}`
        : `${API}/auth/register`;
      const response = await axios.post(url, 
        { name, email, password, language, is_affiliate: isAffiliate },
        { withCredentials: true }
      );
      setUser(response.data);
      localStorage.setItem('orbit-token', response.data.token);
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || 'Registration failed';
      setError(message);
      throw new Error(message);
    }
  };

  const logout = async () => {
    const token = localStorage.getItem('orbit-token');
    try {
      await axios.post(`${API}/auth/logout`, {}, { 
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch {}
    setUser(null);
    localStorage.removeItem('orbit-token');
  };

  const getToken = useCallback(() => {
    return localStorage.getItem('orbit-token');
  }, []);

  return (
    <OrbitAuthContext.Provider value={{
      user, setUser, loading, error, login, register, logout, checkAuth,
      isAuthenticated: !!user,
      token: getToken()
    }}>
      {children}
    </OrbitAuthContext.Provider>
  );
};

export const useOrbitAuth = () => {
  const context = useContext(OrbitAuthContext);
  if (!context) {
    throw new Error('useOrbitAuth must be used within an OrbitAuthProvider');
  }
  return context;
};
